import React, { useState, useEffect } from 'react';
import { auth,database } from './firebase';
import { ref, onValue, query, orderByChild } from 'firebase/database';
import {  useNavigate } from 'react-router-dom';
import { PET_SIZES, PET_STATUS ,PET_TYPES} from './constants';


const PetCard = ({ pet }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) =>
      prevIndex === pet.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? pet.images.length - 1 : prevIndex - 1
    );
  };

  const handleEdit = () => {
    navigate(`/admin/${pet.id}`);
  };

  const handleViewDetails = () => {
    navigate(`/pet/${pet.id}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border-2 border-transparent hover:border-blue-200">
      <div className="relative h-64 sm:h-72 cursor-pointer group" onClick={handleViewDetails}>
        <img
          src={pet.images[currentImageIndex]}
          alt={`${pet.name} - ${pet.type === 'dog' ? 'Perro' : 'Gato'} en adopción`}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {pet.images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-60 text-white rounded-full p-2 sm:p-3 shadow-lg transition-all duration-200 hover:scale-110"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-60 text-white rounded-full p-2 sm:p-3 shadow-lg transition-all duration-200 hover:scale-110"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full">
              {currentImageIndex + 1}/{pet.images.length}
            </div>
          </>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent text-white p-5">
          <h2 className="text-2xl font-bold drop-shadow-lg">
            {pet.name} {PET_TYPES[pet.type]?.icon}
          </h2>
        </div>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {PET_SIZES[pet.size]?.label}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            {pet.sex === 'male' ? '♂ Macho' : '♀ Hembra'}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {pet.age}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            📍 {pet.location}
          </span>
        </div>
        <p className="text-gray-700 mb-4 line-clamp-3 leading-relaxed">{pet.description}</p>
        <div className="flex items-center justify-between gap-3">
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
          pet.active === false ? 'bg-yellow-200 text-yellow-800' : (PET_STATUS[pet.status]?.className || 'bg-gray-200 text-gray-800')
          }`}>
            {pet.active === false ? '⏳ Pendiente' : PET_STATUS[pet.status]?.label}
          </span>

          <div className="flex gap-2">
            <button
             onClick={handleViewDetails}
             className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg">
              Ver más
            </button>

            {auth.currentUser && (auth.currentUser.uid === pet.userId || pet.active === false) && (
              <button
                onClick={handleEdit}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                ✏️ {pet.active === false ? 'Revisar' : 'Editar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PublicPetList = () => {
  const [pets, setPets] = useState([]);
  const [pendingPets, setPendingPets] = useState([]);
  const [filteredPets, setFilteredPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    size: '',
    status: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const petsRef = query(ref(database, 'pets'), orderByChild('createdAt'));
  
  const unsubscribe = onValue(petsRef, (snapshot) => {
    const petsData = [];
    const pendingData = [];
    
    snapshot.forEach((childSnapshot) => {
      const pet = { id: childSnapshot.key, ...childSnapshot.val() };
      if (pet.active === false) {
        pendingData.push(pet);
      } else {
        petsData.push(pet);
      }
    });

    // Filtrar mascotas con status distinto de "adop" y que estén activas
    const filteredPetsData = petsData.filter(pet => pet.status !== "adopt" && pet.status !== "unavailable");

    setPets(filteredPetsData.reverse());  // Reverse para mostrar los más nuevos primero
    setPendingPets(pendingData.reverse());
    setLoading(false);
  }, (error) => {
    console.error("Error fetching pets: ", error);
    setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const filtered = pets.filter(pet => {
      const matchesFilter = Object.entries(filters).every(([key, value]) => {
        return value === '' || pet[key] === value;
      });
      const matchesSearch = pet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            pet.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    });
    setFilteredPets(filtered);
  }, [pets, filters, searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
    </div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-blue-700 via-blue-500 to-amber-500 bg-clip-text text-transparent">
          Mascotas en Adopción
        </h1>
        <p className="text-gray-600 text-lg">Encuentra a tu nuevo mejor amigo</p>
      </div>

      {auth.currentUser && pendingPets.length > 0 && (
        <div className="mb-12 bg-yellow-50 border-2 border-yellow-200 rounded-3xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-3xl">⏳</span>
            <h2 className="text-2xl font-bold text-yellow-800">Solicitudes Pendientes de Revisión</h2>
            <span className="bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full text-sm font-bold">
              {pendingPets.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pendingPets.map((pet) => (
              <PetCard key={pet.id} pet={pet} />
            ))}
          </div>
        </div>
      )}

      <div className="mb-8 bg-white rounded-2xl shadow-lg p-6">
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar por nombre o descripción..."
              className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <select
            name="type"
            onChange={handleFilterChange}
            className="p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200 bg-white cursor-pointer"
          >
            <option value="">🐾 Todos los tipos</option>
            {Object.values(PET_TYPES).map(type => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
          <select
            name="size"
            onChange={handleFilterChange}
            className="p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200 bg-white cursor-pointer"
          >
            <option value="">📏 Todos los tamaños</option>
            {Object.values(PET_SIZES).map(size => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
          <select
            name="status"
            onChange={handleFilterChange}
            className="p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200 bg-white cursor-pointer"
          >
            <option value="">✨ Todos los estados</option>
            {Object.values(PET_STATUS).map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {(filters.type || filters.size || filters.status || searchTerm) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setFilters({ type: '', size: '', status: '' });
                setSearchTerm('');
                document.querySelector('input[type="text"]').value = '';
                document.querySelectorAll('select').forEach(select => select.value = '');
              }}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {filteredPets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 text-lg">No se encontraron mascotas que coincidan con los criterios de búsqueda.</p>
          <p className="text-gray-400 text-sm mt-2">Intenta modificar los filtros o la búsqueda</p>
        </div>
      ) : (
        <>
          <div className="text-center mb-6">
            <p className="text-gray-600">
              Mostrando <span className="font-semibold text-blue-600">{filteredPets.length}</span> mascota{filteredPets.length !== 1 ? 's' : ''} disponible{filteredPets.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPets.map((pet) => (
              <PetCard key={pet.id} pet={pet} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PublicPetList;